import * as React from "react";
import { AlertCircle, MoreHorizontal, Plus, RefreshCw, Trash2, Wallet } from "lucide-react";
import {
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatCard,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  MONEY_RUPEES_MAX,
  boundedNameSchema,
  fieldError,
  moneyRupeesSchema,
} from "@eduviaos/shared";
import { AIDraftButton } from "@eduviaos/capability-ai-ui";
import { useCanManageFees } from "@/hooks/useFeesAuthoring";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import {
  formatRupee,
  useCreateFeePlan,
  useDeleteFeePlan,
  useFeePlans,
  type FeePlan,
} from "@/lib/fees/api";
import { draftRow, useAIDraftGenerator, type FeePlanDraft } from "@/lib/aiDraft";

const PLAN_NAME_MAX = 120;
const PLAN_TYPE_MAX = 40;
const planNameSchema = boundedNameSchema(PLAN_NAME_MAX, "Plan name");
const planTypeSchema = boundedNameSchema(PLAN_TYPE_MAX, "Plan type");
const amountSchema = moneyRupeesSchema;

type BillingCycle = NonNullable<FeePlanDraft["billing_cycle"]>;

const BILLING_CYCLE_OPTIONS: ReadonlyArray<{ value: BillingCycle; label: string }> = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semi_annual", label: "Every six months" },
  { value: "annual", label: "Yearly" },
  { value: "one_time", label: "One time" },
];

function billingCycleLabel(value: string): string {
  return BILLING_CYCLE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

function describeFeePlanDraft(draft: FeePlanDraft) {
  return [
    ...draftRow("Name", draft.name),
    ...draftRow("Base amount", draft.base_amount === null ? null : formatRupee(draft.base_amount)),
    ...draftRow("Plan type", draft.plan_type),
    ...draftRow("Billing", draft.billing_cycle ? billingCycleLabel(draft.billing_cycle) : null),
  ];
}

function amountError(raw: string): string | null {
  if (raw.trim() === "") return "Enter an amount in rupees.";
  return fieldError(amountSchema, raw);
}

export function FeesPage() {
  const { canManageFees, missingView } = useCanManageFees();
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "fee-plans",
    defaultPageSize: 10,
  });
  const [createOpen, setCreateOpen] = React.useState(false);
  const [name, setName] = React.useState("");
  const [baseAmount, setBaseAmount] = React.useState("1500");
  const [planType, setPlanType] = React.useState("hourly_slot");
  const [billingCycle, setBillingCycle] = React.useState<BillingCycle>("monthly");
  const generateFeePlanDraft = useAIDraftGenerator("fee_plan");
  const [showErrors, setShowErrors] = React.useState(false);
  const nameError = fieldError(planNameSchema, name);
  const baseAmountError = amountError(baseAmount);
  const planTypeError = fieldError(planTypeSchema, planType);

  const plansQuery = useFeePlans({ limit: pageSize, offset: page * pageSize });
  const createMutation = useCreateFeePlan();
  const deleteMutation = useDeleteFeePlan();

  const total = plansQuery.data?.total ?? 0;
  const items = React.useMemo(() => plansQuery.data?.items ?? [], [plansQuery.data?.items]);
  const activeCount = items.filter((p) => p.is_active).length;

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    setShowErrors(true);
    if (nameError || baseAmountError || planTypeError) return;
    const amount = Number(baseAmount);
    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        plan_type: planType.trim(),
        base_amount: amount,
        duration_hours: planType === "hourly_slot" ? 3 : null,
        duration_tier: planType === "hourly_slot" ? "3hr" : null,
        billing_cycle: billingCycle,
        payment_methods: ["upi", "card"],
      });
      toast.success(`Created fee plan "${name.trim()}".`);
      setCreateOpen(false);
      setName("");
      setBillingCycle("monthly");
      setShowErrors(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not create fee plan.");
    }
  };

  const applyFeePlanDraft = (draft: FeePlanDraft) => {
    if (draft.name) setName(draft.name);
    if (draft.base_amount !== null) setBaseAmount(String(draft.base_amount));
    if (draft.plan_type) setPlanType(draft.plan_type);
    if (draft.billing_cycle) setBillingCycle(draft.billing_cycle);
  };

  const handleDelete = async (plan: FeePlan) => {
    try {
      await deleteMutation.mutateAsync(plan.id);
      toast.success(`Archived "${plan.name}".`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not archive fee plan.");
    }
  };

  if (missingView) {
    return (
      <div className="mx-auto max-w-3xl py-16 text-center">
        <AlertCircle className="text-muted-foreground mx-auto mb-3 size-8" aria-hidden="true" />
        <p className="text-muted-foreground text-sm">
          You don&apos;t have permission to view fees.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl py-8">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Fees</PageHeaderHeading>
            <PageHeaderDescription>
              Fee plans for your study centre — hourly slots, monthly passes, and per-session
              pricing.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void plansQuery.refetch()}
              disabled={plansQuery.isFetching}
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", plansQuery.isFetching && "animate-spin")}
              />
              Refresh
            </Button>
            {canManageFees ? (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus aria-hidden="true" className="size-4" />
                New plan
              </Button>
            ) : null}
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <StatCard
          label="Active plans"
          value={String(activeCount)}
          icon={<Wallet aria-hidden="true" />}
        />
        <StatCard label="Total plans" value={String(total)} icon={<Wallet aria-hidden="true" />} />
        <StatCard
          label="On this page"
          value={String(items.length)}
          icon={<Wallet aria-hidden="true" />}
        />
      </div>

      <div className="mt-6">
        <DataTable>
          <DataTableToolbar>
            <span className="text-muted-foreground text-sm">Fee plans</span>
            <DataTableToolbarSpacer />
          </DataTableToolbar>
          {plansQuery.isLoading ? (
            <DataTableSkeleton columns={5} rows={5} />
          ) : plansQuery.isError ? (
            <div className="border-border/60 bg-muted/30 text-muted-foreground rounded-md border p-6 text-sm">
              Could not load fee plans.{" "}
              <button type="button" className="underline" onClick={() => void plansQuery.refetch()}>
                Retry
              </button>
            </div>
          ) : items.length === 0 ? (
            <DataTableEmpty
              title="No fee plans yet"
              description="Create your first plan so learners can be assigned pricing."
              action={
                canManageFees ? (
                  <Button size="sm" onClick={() => setCreateOpen(true)}>
                    New plan
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <DataTableScroll>
                <Table>
                  <caption className="sr-only">Fee plans</caption>
                  <TableHeader className="bg-background sticky top-0 z-10">
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Billing</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[1%]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody className="divide-border/60 divide-y">
                    {items.map((plan) => (
                      <TableRow key={plan.id} className="hover:bg-muted/40">
                        <TableCell className="font-medium">{plan.name}</TableCell>
                        <TableCell>{plan.plan_type}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatRupee(plan.base_amount)}
                        </TableCell>
                        <TableCell>{plan.billing_cycle}</TableCell>
                        <TableCell>
                          <StatusPill status={plan.is_active ? "active" : "inactive"} />
                        </TableCell>
                        <TableCell className="text-right">
                          {canManageFees ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="Plan actions">
                                  <MoreHorizontal className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => void handleDelete(plan)}
                                >
                                  <Trash2 className="size-4" aria-hidden="true" />
                                  Archive
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DataTableScroll>
              <DataTableFooter>
                <DataTablePagination
                  page={page + 1}
                  pageSize={pageSize}
                  totalRows={total}
                  onPageChange={(next) => setPage(Math.max(0, next - 1))}
                  onPageSizeChange={(next) => {
                    setPageSize(next);
                    setPage(0);
                  }}
                />
              </DataTableFooter>
            </>
          )}
        </DataTable>
      </div>

      <Dialog
        open={createOpen}
        onOpenChange={(next) => {
          setCreateOpen(next);
          if (!next) setShowErrors(false);
        }}
      >
        <DialogContent>
          <DialogHeader className="flex-row items-center justify-between gap-3 space-y-0 pr-8">
            <DialogTitle>New fee plan</DialogTitle>
            <AIDraftButton<FeePlanDraft>
              title="Draft a fee plan"
              placeholder="e.g. Monthly plan for evening learners, ₹1,800, due on the 5th"
              generate={generateFeePlanDraft}
              describe={describeFeePlanDraft}
              onApply={applyFeePlanDraft}
              data-testid="fee-plan-ai-draft"
            />
          </DialogHeader>
          <form noValidate onSubmit={(e) => void handleCreate(e)}>
            <div className="grid gap-4 py-2">
              <FeeField id="fee-plan-name" label="Name" error={showErrors ? nameError : null}>
                <Input
                  id="fee-plan-name"
                  value={name}
                  maxLength={PLAN_NAME_MAX}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Daily 3-hour slot"
                  aria-invalid={showErrors && nameError ? true : undefined}
                  aria-describedby={showErrors && nameError ? "fee-plan-name-error" : undefined}
                />
              </FeeField>
              <FeeField
                id="fee-plan-amount"
                label="Base amount (₹)"
                error={showErrors ? baseAmountError : null}
              >
                <Input
                  id="fee-plan-amount"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={MONEY_RUPEES_MAX}
                  step="0.01"
                  value={baseAmount}
                  onChange={(e) => setBaseAmount(e.target.value)}
                  aria-invalid={showErrors && baseAmountError ? true : undefined}
                  aria-describedby={
                    showErrors && baseAmountError ? "fee-plan-amount-error" : undefined
                  }
                />
              </FeeField>
              <FeeField
                id="fee-plan-type"
                label="Plan type"
                error={showErrors ? planTypeError : null}
              >
                <Input
                  id="fee-plan-type"
                  value={planType}
                  maxLength={PLAN_TYPE_MAX}
                  onChange={(e) => setPlanType(e.target.value)}
                  aria-invalid={showErrors && planTypeError ? true : undefined}
                  aria-describedby={showErrors && planTypeError ? "fee-plan-type-error" : undefined}
                />
              </FeeField>
              <FeeField id="fee-plan-billing" label="Billing" error={null}>
                <Select
                  value={billingCycle}
                  onValueChange={(v) => setBillingCycle(v as BillingCycle)}
                >
                  <SelectTrigger id="fee-plan-billing">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BILLING_CYCLE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FeeField>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FeeField({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? (
        <p id={`${id}-error`} className="text-destructive text-xs">
          {error}
        </p>
      ) : null}
    </div>
  );
}
